import { Box, Card, CircularProgress, Grid, Typography, Button, List, ListItem } from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { DocumentReferenceResponse } from "api/generatedApi";
import { DocumentRow } from "./document-row";
import { useEffect, useRef, useState } from "react";
import { ScreenSizeEnum } from "shared/use-screen-size";
import { UploadDialog } from "shared/editors/upload-Dialog";
import { useDialog } from "shared/dialog/use-dialog";
import { useTranslation } from "react-i18next";
import { useSortDocuments } from "./hooks/use-sort-documents";

export interface UploadFieldProps {
  documents: DocumentReferenceResponse[];
  allowDownloads: boolean;
  disabled?: boolean; // disables the upload button
  title?: string;
  noContentText?: string;
  maxViewDocuments?: number;
  isUploading?: boolean;
  handleFileChange?: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  canUploadDocument: () => boolean;
  screenSize: ScreenSizeEnum;
  inADialog?: boolean; // if true, the upload field is displayed in a dialog
  canDeleteDocument: () => boolean; // function to check if the document can be deleted
  clickDocumentDelete?: (document: DocumentReferenceResponse) => void; // function to delete a document
  textColor?: string; // optional prop to set text color
  disablePadding?: boolean; // if true, disables padding around the card
  titelVariant?: "caption" | "body1" | "body2" | "h6" | "h5" | "h4" | "h3" | "h2" | "h1"; // size of the title text, default is body1
  titelWeight?: "bold" | "normal"; // weight of the title text, default is bold
  borderRadius?: string; // border radius of the card, default is 10px
  allowDownloadWhileInDialog?: boolean;
}

export const UploadField = ({
  canDeleteDocument,
  clickDocumentDelete,
  allowDownloadWhileInDialog = false,
  titelVariant,
  titelWeight,
  borderRadius,
  inADialog = false,
  canUploadDocument,
  isUploading = false,
  documents,
  allowDownloads = true,
  disabled = false,
  title = "",
  noContentText = "",
  maxViewDocuments = 3,
  handleFileChange,
  textColor = "primary.main",
  disablePadding,
}: UploadFieldProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [openDialog, closeDialog] = useDialog(UploadDialog);
  const { t } = useTranslation();
  const sortDocuments = useSortDocuments();
  const [documentsList, setDocuments] = useState<DocumentReferenceResponse[]>(documents ? [...documents] : []);
  const [docListSort, setDocListSort] = useState<DocumentReferenceResponse[]>([]);

  const fileUploadIcon = (
    <Box sx={{ position: "relative" }}>
      <FileUploadOutlinedIcon sx={{ color: "primary.main" }} />
      {isUploading && <CircularProgress size={30} sx={{ position: "absolute", top: -3, left: -3, zIndex: 1 }} />}
    </Box>
  );

  const shownDocuments = documentsList?.length > maxViewDocuments ? sortDocuments(documentsList).slice(0, maxViewDocuments) : documentsList;
  const padLeft = "30px";
  const padRight = "30px";
  const padTop = "30px";
  const padBottom = "10px";

  useEffect(() => {
    setDocuments(documents ? [...documents] : []);
    setDocListSort(sortDocuments(documents ? [...documents] : []));
  }, [documents]);

  // clicks on the hidden file input to open the file dialog
  const handleUpload = () => {
    if (isUploading) return;
    if (!fileInputRef || !fileInputRef.current) return;
    fileInputRef.current.click();
  };

  const showMoreActionClicked = () => {
    if (shownDocuments) {
      openDialog({ documents: documents, allowDownloads, onClose: closeDialog, canDeleteDocument });
    }
  };

  return (
    <Box>
      <Card
        sx={{
          borderStyle: "none",
          position: "relative",
          pl: !disablePadding ? padLeft : 0,
          pr: !disablePadding ? padRight : 0,
          pb: !disablePadding ? padBottom : 0,
          pt: !disablePadding ? padTop : 0,
          borderRadius: borderRadius || "10px",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" height="auto" sx={{ pb: padBottom }}>
          <Typography justifySelf="center" variant={titelVariant || "body1"} color={"primary.main"} fontWeight={titelWeight || "bold"}>
            {title}
          </Typography>
          {!disabled && canUploadDocument() && (
            <Box component="span" sx={{ cursor: isUploading ? "not-allowed" : "pointer" }} onClick={handleUpload}>
              {fileUploadIcon}
            </Box>
          )}
        </Box>
        {inADialog ? (
          <Box sx={{ maxHeight: "150px", overflow: "auto" }}>
            <List sx={{ width: "100%" }}>
              {docListSort && docListSort.length > 0 ? (
                docListSort.map((doc) => (
                  <ListItem key={doc.documentId} disablePadding>
                    <DocumentRow
                      allowDownloadWhileInDialog={allowDownloadWhileInDialog}
                      textColor={textColor}
                      clickDocumentDelete={clickDocumentDelete}
                      inADialog={inADialog}
                      allowDownload={allowDownloads}
                      document={doc}
                      canDeleteDocument={canDeleteDocument}
                    />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body1" color="grey.100">
                  {noContentText}
                </Typography>
              )}
            </List>
          </Box>
        ) : (
          <Grid container>
            {shownDocuments && shownDocuments.length > 0 ? (
              shownDocuments.map((doc) => <DocumentRow textColor={textColor} key={doc.documentId} allowDownload={allowDownloads} document={doc} canDeleteDocument={canDeleteDocument} />)
            ) : (
              <Typography variant="body1" color="grey.100">
                {noContentText}
              </Typography>
            )}
          </Grid>
        )}
        {/* // Hidden file input to trigger file selection dialog */}
        <input style={{ display: "none" }} ref={fileInputRef} type="file" accept="application/pdf, .png, .jpg, .jpeg" onChange={handleFileChange} />
      </Card>

      {shownDocuments && !inADialog && documentsList.length > maxViewDocuments && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="text" color="primary" onClick={showMoreActionClicked} sx={{ pr: padRight }}>
            {t("common.viewMore")}
          </Button>
        </Box>
      )}
    </Box>
  );
};
